import React, { useEffect, useMemo, useRef } from 'react';

import { graphql, useFragment } from 'react-relay';

import { UserInlinePreview } from '@atlassian/user-inline-preview/UserInlinePreview';
import { UserInlinePreviewList } from '@atlassian/user-inline-preview/UserInlinePreviewList';

import { useLuminaAnalyticsEvents } from '../../../analytics/use-lumina-analytics-events';
import type {
	luminaUserInlinePreviewListRelay_knowledgeDiscovery$data,
	luminaUserInlinePreviewListRelay_knowledgeDiscovery$key,
} from './__generated__/luminaUserInlinePreviewListRelay_knowledgeDiscovery.graphql';

type UserInlinePreviewListRelayProps = Omit<
	React.ComponentProps<typeof UserInlinePreviewList>,
	'children'
> & {
	cloudId: string;
	knowledgeDiscoveryFragment?: luminaUserInlinePreviewListRelay_knowledgeDiscovery$key;
	orgId: string;
	requestingUserId?: string;
};

export function normalizeUserSearchRows(
	data: luminaUserInlinePreviewListRelay_knowledgeDiscovery$data | null | undefined,
): {
	accountId: string;
	avatarUrl: string | null;
	email: null;
	jobTitle: string | null;
	name: string;
	thirdPartyIntegrations:
		| {
				externalId: string;
				integrationId: string;
				product: string;
		  }[]
		| undefined;
}[] {
	if (
		!data ||
		!data.searchUsersV2 ||
		data.searchUsersV2.__typename !== 'KnowledgeDiscoverySearchUsers'
	) {
		return [];
	}
	const users =
		data.searchUsersV2.users?.filter((user): user is NonNullable<typeof user> => user != null) ??
		[];
	return users.map((user) => ({
		accountId: user.id,
		name: user.name,
		avatarUrl: user.avatarUrl ?? null,
		jobTitle: user.title ?? null,
		email: null,
		thirdPartyIntegrations: user.thirdPartyIntegrations?.map((integration) => ({
			externalId: integration.externalId,
			integrationId: integration.integrationId,
			product: integration.product,
		})),
	}));
}

/**
 * Relay-connected {@link UserInlinePreviewList} backed by Knowledge Discovery `userSearchV2`.
 * Spread `luminaUserInlinePreviewListRelay_knowledgeDiscovery` on `knowledgeDiscovery` in the parent query
 * and pass fragment variables `accountIds`, `requestingUserId`, and `tenantId`.
 */
export function UserInlinePreviewListRelay({
	knowledgeDiscoveryFragment,
	cloudId,
	orgId,
	requestingUserId,
	...listProps
}: UserInlinePreviewListRelayProps): React.JSX.Element {
	const userSearch = useFragment(
		graphql`
			fragment luminaUserInlinePreviewListRelay_knowledgeDiscovery on KnowledgeDiscoveryQueryApi
			@argumentDefinitions(
				accountIds: { type: "[String!]!" }
				requestingUserId: { type: "String!" }
				tenantId: { type: "String!" }
			) {
				searchUsersV2(
					accountIds: $accountIds
					requestingUserId: $requestingUserId
					tenantId: $tenantId
				) @optIn(to: "KnowledgeDiscovery Search Users V2") {
					__typename
					... on KnowledgeDiscoverySearchUsers {
						users {
							id
							name
							avatarUrl
							title
							thirdPartyIntegrations {
								externalId
								integrationId
								product
							}
						}
					}
					... on QueryError {
						__typename
					}
				}
			}
		`,
		knowledgeDiscoveryFragment ?? null,
	);

	const rows = useMemo(() => normalizeUserSearchRows(userSearch), [userSearch]);

	const { fireEvent } = useLuminaAnalyticsEvents();
	const hasFiredRef = useRef(false);

	useEffect(() => {
		if (hasFiredRef.current || rows.length === 0) {
			return;
		}
		hasFiredRef.current = true;
		fireEvent('operational.lumina.relayLoaded', { componentType: 'userInlinePreviewList' });
	}, [fireEvent, rows.length]);

	if (rows.length === 0) {
		throw new Error('Lumina UserInlinePreviewList: no users resolved');
	}

	return (
		<UserInlinePreviewList {...listProps}>
			{rows.map((row) => (
				<UserInlinePreview
					key={row.accountId}
					cloudId={cloudId}
					orgId={orgId}
					data={row}
					requestingUserId={requestingUserId}
				/>
			))}
		</UserInlinePreviewList>
	);
}
